// Read/write a <label>'s own text without disturbing the elements inside it.
//
// Form labels in the contact modal are no longer text-only: they can carry a
// provenance marker (shared/registry/_provenance_marker.html.erb) and/or a `?`
// help icon, both element children containing a Material Symbols ligature.
// Plain DOM text APIs get this wrong in both directions, and both mistakes
// actually shipped:
//
//   `label.textContent = "…"` replaces ALL children with one text node, so the
//   marker is silently destroyed (entity_type_toggle_controller ran this on
//   every modal open, before the user did anything).
//
//   `label.textContent` reads through into the icon span, so the ligature NAME
//   leaks out as literal text — "Ulica account_balance" in the register-diff
//   panel (contacts/register_diff_controller).
//
// Both functions therefore operate on the label's DIRECT child text nodes only.
// That is exactly the "text the author wrote in this label" in every form label
// we render: markers, icons and any other decoration are always wrapped in an
// element, never bare text.

function directTextNodes(element) {
  return Array.from(element.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE)
}

// The label's own visible text, whitespace-collapsed. ERB indentation puts
// newlines and runs of spaces around the text, and a marker can split it into
// several nodes, so the parts are joined with a single space before collapsing
// rather than concatenated — otherwise "Foo <marker> bar" would read "Foobar".
export function readLabelText(label) {
  if (!label) return ""

  return directTextNodes(label)
    .map((node) => node.textContent)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim()
}

// Replaces the label's own text, leaving every element child (marker, help
// icon) in place. The text is written into the FIRST existing text node so it
// keeps its position relative to those children — the markers render after the
// text, and their leading gap comes from `.provenance-marker { margin-left }`,
// not from a whitespace text node, so no padding needs to be preserved here.
export function setLabelText(label, text) {
  if (!label) return

  const [first, ...rest] = directTextNodes(label)
  for (const node of rest) node.remove()

  if (first) {
    first.textContent = text
  } else {
    label.prepend(document.createTextNode(text))
  }
}
